import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Layout from "../component/layout";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { toastify } from "../../../utils/toast";
import { getItem } from "../../../utils/localStorage";
import { makeRequest2 } from "../../../utils/makeRequest";
import { 
    getPropertyManagedCustomersURL
} from "../../../utils/urls";

const PropertyManagedCustomers = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedType, setSelectedType] = useState("");

    // Fetch customers data
    const fetchCustomersData = useCallback(async () => {
        try {
            setLoading(true);
            const facilityId = await getItem("selectedFacilityId");
            
            if (!facilityId) {
                toastify("No facility selected", "error");
                return;
            }

            console.log('Fetching customers data from:', `${getPropertyManagedCustomersURL}/${facilityId}`);

            const response = await makeRequest2(
                `${getPropertyManagedCustomersURL}/${facilityId}`, 
                'GET'
            );

            console.log('Customers fetch response:', response);

            if (response?.success && response.data) {
                const customersData = response.data.customers || [];
                
                // Filter to show only home owners
                const homeOwnerCustomers = customersData.filter(customer => 
                    customer.customerType === 'home owner'
                );
                
                setCustomers(homeOwnerCustomers);
                
                console.log('Home owner customers data loaded successfully:', homeOwnerCustomers);
                
                if (homeOwnerCustomers.length > 0) {
                    toastify(`Loaded ${homeOwnerCustomers.length} home owner clients successfully`, "success");
                } else {
                    toastify("No home owner clients found in property managed apartments", "info");
                }
            } else {
                console.log('No customers data found, setting empty state');
                setCustomers([]);
                toastify(response?.data?.message || "No customers found", "info");
            }
        } catch (error) {
            console.error('Error fetching customers data:', error);
            toastify("Error fetching customers data: " + error.message, "error");
            setCustomers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Handle search functionality
    const handleSearch = (value) => {
        setSearchTerm(value);
        filterCustomers(value, selectedType);
    };

    // Handle type change
    const handleTypeChange = (type) => {
        setSelectedType(type);
        filterCustomers(searchTerm, type);
    };

    // Filter customers
    const filterCustomers = (search, type) => {
        // This would be implemented based on your filtering logic
        // For now, just basic filtering
    };

    // Client name template with navigation
    const clientNameTemplate = (rowData) => {
        const fullName = rowData.fullName || `${rowData.firstName} ${rowData.lastName}`;
        return (
            <Link 
                to={`/facility/customer_management/view_customer/${rowData._id}`} 
                style={{ color: 'royalblue', textDecoration: 'none' }}
            >
                {fullName}
            </Link>
        );
    };

    const customerTypeTemplate = (rowData) => {
        const isLandlord = rowData.customerType === 'home owner';
        return (
            <span className={`badge ${isLandlord ? 'bg-success' : 'bg-info'}`}>
                {isLandlord ? 'Landlord' : 'Tenant'}
            </span>
        );
    };

    const contactTemplate = (rowData) => {
        return rowData.phoneNumber || 'No Phone';
    };

    const emailTemplate = (rowData) => {
        return rowData.email || 'No Email';
    };

    // Apartments template with navigation to specific apartments
    const unitsTemplate = (rowData) => {
        if (!rowData.unitsManaged || rowData.unitsManaged.length === 0) {
            return 'No apartments';
        }

        return (
            <div className="d-flex flex-column gap-1">
                {rowData.unitsManaged.map((unit, index) => (
                    <Link 
                        key={index}
                        to={`/facility/unit_management/view_unit/${unit.unitId}`}
                        style={{ color: 'royalblue', textDecoration: 'none' }}
                    >
                        {unit.unitName}
                    </Link>
                ))}
            </div>
        );
    };

    const statusTemplate = (rowData) => {
        const isActive = rowData.status === 'Active';
        return (
            <span className={`badge ${isActive ? 'bg-success' : 'bg-secondary'}`}>
                {rowData.status || 'Unknown'}
            </span>
        );
    };

    const actionsTemplate = (rowData) => {
        return (
            <div className="d-flex gap-2">
                <Button
                    icon="ti ti-file-text"
                    className="p-button-rounded p-button-info"
                    tooltip="View Contract"
                    onClick={() => console.log('View contract for:', rowData)}
                />
                <Button
                    icon="ti ti-receipt"
                    className="p-button-rounded p-button-success"
                    tooltip="View Invoice"
                    onClick={() => console.log('View invoice for:', rowData)}
                />
            </div>
        );
    };

    // Load data on component mount
    useEffect(() => {
        fetchCustomersData();
    }, [fetchCustomersData]);

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/facility/">Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to="">Property Management</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    Managed Clients
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header">
                            <h5>Property Managed Clients</h5>
                        </div>
                        <div className="card-body">
                            <div className="row mb-3">
                                <div className="col-md-3">
                                    <input
                                        className="form-control"
                                        placeholder="Search clients"
                                        value={searchTerm}
                                        onChange={(e) => handleSearch(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-control"
                                        value={selectedType}
                                        onChange={(e) => handleTypeChange(e.target.value)}
                                    >
                                        <option value="">All Status</option>
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <Button
                                        label={loading ? "Loading..." : "Refresh"}
                                        icon={loading ? "ti ti-loader animate-spin" : "ti ti-refresh"}
                                        className="p-button-primary float-end"
                                        onClick={fetchCustomersData}
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            <DataTable
                                value={customers}
                                paginator
                                rows={10}
                                loading={loading}
                                emptyMessage="No clients found"
                                sortMode="multiple"
                                removableSort
                                stripedRows
                                tableStyle={{ minWidth: '50rem' }}
                            >
                                <Column 
                                    field="fullName" 
                                    header="Client Name" 
                                    sortable 
                                    body={clientNameTemplate}
                                />
                                <Column 
                                    field="customerType" 
                                    header="Type" 
                                    body={customerTypeTemplate}
                                    sortable 
                                />
                                <Column 
                                    field="phoneNumber" 
                                    header="Phone" 
                                    body={contactTemplate}
                                />
                                <Column 
                                    field="email" 
                                    header="Email" 
                                    body={emailTemplate}
                                />
                                <Column 
                                    field="unitsManaged" 
                                    header="Apartments" 
                                    body={unitsTemplate}
                                />
                                <Column 
                                    field="status" 
                                    header="Status" 
                                    body={statusTemplate}
                                    sortable 
                                />
                                {/* <Column 
                                    header="Actions" 
                                    body={actionsTemplate}
                                /> */}
                            </DataTable>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default PropertyManagedCustomers;